<template>
    <div>
        <TitleBar>
            <template v-slot:title><translate>Hours Report</translate></template>
            <template v-slot:buttons>
                <GekkoButton 
                    :text="$gettext('Hours')" 
                    :to="{ name: 'Hours' }"
                    icon="fa-solid fa-up-right-from-square"
                    :icon-right="true"
                    variant="link"
                    class="mr-2"
                    />

                <b-dropdown right variant="outline-primary">
                    <template #button-content>
                        <font-awesome-icon icon="fa-solid fa-cloud-download" /><span v-translate>Download</span>
                    </template>
                    <b-dropdown-item @click="exportHours('csv')" v-b-tooltip.hover.right>
                        <font-awesome-icon icon="fa-regular fa-file" /><span v-translate>as excel file (.xls)</span>
                    </b-dropdown-item>
                    <b-dropdown-item @click="exportHours('pdf')" class="act" v-b-tooltip.hover.right>
                        <font-awesome-icon icon="fa-regular fa-file-zipper" /><span v-translate>as pdf file (.pdf)</span>
                    </b-dropdown-item>
                </b-dropdown>
            </template>
        </TitleBar>
        <div class="content">
            <HourEditModal 
                v-if="selectedHour" 
                v-model="hourCreationState" 
                :selectedEvent="selectedHour" 
                @delete-event="onDelete" 
                @delete-recurrent-event="onDeleteRecurrentHour"
                @save-hour="onSubmit" 
                @save-recurrent-hour="onSubmitRecurrentHour"
                @cancel="cancelEventCreation" />

            <CopyHourModal ref="copy_hour_modal" @copyHour="onCopyHour" :hour="hourToCopy" />

            <ConfirmationDialog 
                ref="recurrent_hour_update_confirmation_modal"
                :title="recurrentHourUpdateConfirmationTitle"
                :body="recurrentHourUpdateConfirmationBody"
                :options="recurrentHourUpdateConfirmationOptions"
                @cancel="cancelConfirmationDialog"
                @confirmed="onRecurrentHourUpdateConfirmation" />
                
            <ConfirmationDialog 
                ref="recurrent_hour_delete_confirmation_modal"
                :title="recurrentHourDeleteConfirmationTitle"
                :body="recurrentHourDeleteConfirmationBody"
                :options="recurrentHourDeleteConfirmationOptions"
                @cancel="cancelConfirmationDialog"
                @confirmed="onRecurrentHourDeleteConfirmation" />
            
            <TimezonePickerModal ref="timezone_picker_modal" :exportFormat="exportFormat" @timezoneSelected="onTimezoneSelected"></TimezonePickerModal>

            <!-- TODO: make them secondary filters? -->
            <FilterBar 
                cookieName="HoursReportFilterOptionsVue" 
                :filter-bar-type="'hours-report'"
                :mainFilters="[ 'DateRangeFilter', ]"
                :secondaryFilters="[ 'UserFilter', 'HourStatusFilter', 'HoursCustomerFilter', 'ActiveHoursSelector', 'HoursSorting' ]"
                @change="filtersChanged"
                >
                <template v-slot:right-bar>
                    <HourTotalsContainer />
                </template>
            </FilterBar>
            <div v-if="hourList.length" class="py-4">
                <CashflowChart :data="chartData" :options="chartOptions" :key="chartKey" />
            </div>

            <HourListTable 
                :hour-list="hourList" 
                @row-clicked="editHour" 
                @new-event="addHour" 
                @copyHour="openCopyModal" 
                @delete-recurrent-event="onDeleteRecurrentHour"
                @delete-event="onDelete"
                @updated="refreshData" />

            <GekkoButton
                v-if="nextHoursUrl"
                id="load_more_button"
                :text="$gettext('Load more hours')"
                icon="fa-solid fa-plus"
                variant="link"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import TitleBar from "@/components/TitleBar";
import HourListTable from "@/components/hours/HourListTable";
import HourService from "@/store/services/hour.service.js";
import FilterBar from "@/components/list_filters/FilterBar";
import GekkoButton from "@/components/GekkoButton";
import CashflowChart from '@/components/dashboard/charts/Cashflow';
import HourEditModal from "@/components/hours/HourEditModal";
import CopyHourModal from "@/components/hours/CopyHourModal";
import ConfirmationDialog from "@/components/ui/ConfirmationDialog";
import TimezonePickerModal from "@/components/hours/TimezonePickerModal";
import HourTotalsContainer from "@/components/hours/HourTotalsContainer";

export default {
    name: "HoursReport",
    components: {TitleBar, HourListTable, FilterBar, HourTotalsContainer, GekkoButton, CashflowChart, HourEditModal, CopyHourModal, ConfirmationDialog, TimezonePickerModal},
    data() {
        return {
            hourList: [],
            filters: {},
            chartData: {},
            chartOptions: {
                title: { display: false },
                scales: {
                    xAxes: [{
                            stacked: true,
                            beginAtZero: true,
                            barPercentage: 0.9,
                            id: 'x-axis-label',
                            position: 'bottom',
                            scaleStartValue: 0,
                            gridLines: {
                                display: false
                            },
                        }],
                        yAxes: [{
                                stacked: true,
                                id: 'y-axis-label',
                                ticks: {
                                    beginAtZero: true,
                                    callback: (value) => {
                                            return value
                                        },
                                },
                                position: 'left',
                                gridLines: {
                                    color: "#f9f9f9",
                                    display: true,
                                    drawBorder: false,
                                    zeroLineColor: "#e5e5e5",
                                    zeroLineWidth: 1
                                },
                        }]
                },
                legend: {
                    display: true,
                    position: 'bottom'
                },
                maintainAspectRatio: false,
                scaleBeginAtZero: true,
                tooltips: {
                    callbacks: {
                        title: (tooltipItem, data) => {
                            return data['labels'][tooltipItem[0]['index']] + ' ' + data['datasets'][tooltipItem[0]['datasetIndex']]['label'];
                        },
                        label: (tooltipItem) => {
                            return tooltipItem.value
                        }
                    },
                    backgroundColor: '#22AC8A',
                    titleFontSize: 14,
                    cornerRadius: 8,
                    titleFontColor: '#ffffff',
                    bodyFontColor: '#ffffff',
                    bodyFontSize: 14,
                    displayColors: false
                }
            },
            chartKey: 0,
            hourCreationState: null,
            selectedHour: null,
            fetchingHours: false,
            nextHoursUrl: null,
            exportFormat: null,
            

            // Modal variables
            hourToCopy: null,

            recurrentHourUpdateConfirmationTitle: this.$gettext("You are adjusting a recurring entry"),
            recurrentHourUpdateConfirmationBody: this.$gettext("Do you also want to change the entries planned after this one?"),
            recurrentHourUpdateConfirmationOptions: [
                {key: "one", text: this.$gettext("Change only this entry")},
                {key: "all", text: this.$gettext("Change this entry and all subsequent entries")}
            ],
            recurrentUpdateResult: null,

            recurrentHourDeleteConfirmationTitle: this.$gettext("You are deleting a recurring entry"),
            recurrentHourDeleteConfirmationBody: this.$gettext("Do you also want to delete the entries planned after this one?"),
            recurrentHourDeleteConfirmationOptions: [
                {key: "one", text: this.$gettext("Delete only this entry")},
                {key: "all", text: this.$gettext("Delete this entry and all subsequent entries")}
            ],
            recurrentDeleteResult: null
        }
    },
    computed: {
        ...mapGetters ({ 
            currentUser: 'main/getCurrentUser',
            getCompanyId: 'main/getCompanyId'
        }),
    },
    created() {
        window.addEventListener('scroll', this.handleScroll);
    },
    destroyed() {
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        refreshData() {
            this.$store.commit("filterStore/SET_CALENDAR_VIEW", 'report');
            this.getHours();
            this.getChartData();
            this.$store.commit("hourStore/SET_REFRESH_TOTALS", { refresh: null, exclude: null });
        },
        _prepareFilters() {
            let filters = { ...this.filters }
            filters.extended = "True";
            filters.ordering = 'date';

            if(this.$store.getters["filterStore/getFilter"].created_by) {
                filters.created_by = this.$store.getters["filterStore/getFilter"].created_by;
            }

            filters.company_profile = this.getCompanyId;

            if(!filters.project && this.$store.getters["filterStore/getCurrentCustomer"]) {
                filters.project__company = this.$store.getters["filterStore/getCurrentCustomer"];
            }

            if (filters.status === 'unassigned')
                filters.assignment_status = 'unassigned';
            else if (filters.status === 'invoiced' || filters.status === 'uninvoiced')
                filters.assignment_status = 'assigned';
            else
                filters.assignment_status = '';

            return filters;
        },
        getHours() {
            let filters = this._prepareFilters();
            filters.ordering = filters.hours_ordering;
            HourService.list(filters).then((response) => {
                this.hourList = response.data.results;
                
                if (response.data.next)
                    this.nextHoursUrl = response.data.next;
                else
                    this.nextHoursUrl = null;
            });
        },
        getChartData() {
            this.chartData = {
                labels: [],
                datasets: [
                    {
                        status: "invoiced",
                        data: [],
                        type: "bar",
                        label: this.$gettext('Invoiced'),
                        backgroundColor: "#8ec45b"
                    },
                    {
                        status: "uninvoiced",
                        data: [],
                        type: "bar",
                        label: this.$gettext('Uninvoiced'),
                        backgroundColor: "#913b55"
                    },
                    {
                        status: "unassigned",
                        data: [],
                        type: "bar",
                        label: this.$gettext('Unassigned'),
                        backgroundColor: "#ddd"
                    }
                ]
            }
            
            let filters = this._prepareFilters();
            HourService.getTotalGroupedHours(filters).then((response) => {
                let dates = Object.keys(response.data);
                dates.forEach((date) => {
                    this.chartData.labels.push(date);

                    let invoicedItem = this.chartData.datasets.find((item) => item.status === "invoiced");
                    invoicedItem.data.push(response.data[date]["invoiced"]);

                    let uninvoicedItem = this.chartData.datasets.find((item) => item.status === "uninvoiced");
                    uninvoicedItem.data.push(response.data[date]["uninvoiced"]);

                    let unassaignedItem = this.chartData.datasets.find((item) => item.status === "unassigned");
                    unassaignedItem.data.push(response.data[date]["unassigned"]);

                    this.chartKey++;
                });
            });
        },
        filtersChanged(filters) {
            this.filters = filters;
            this.refreshData();
        },
        exportHours(exportFormat) {
            this.exportFormat = exportFormat;
            this.$refs.timezone_picker_modal.showModal = true;
        },
        onTimezoneSelected(exportOption) {
            let filters = this._prepareFilters();
            filters.timezone = exportOption.name; // timezone name
            filters.rounding_precision = exportOption.rounding_precision;
            filters.company_profile = this.getCompanyId;
            
            let url;
            if (this.exportFormat === "csv"){
                url = HourService.export_csv(filters);
            }else if (this.exportFormat === "pdf"){
                if (!this.$store.getters['getIsPremium']) {
                  this.$store.commit('togglePremiumModal', 'web_costs_smartscan');
                  return;
                }
                url = HourService.export_pdf(filters);
            }
            window.location.href = url;
        },
        editHour(hour) {
            this.selectedHour = hour;
            this.hourCreationState = 'edit';
        },
        addHour() {
            this.selectedHour = {
                start: new Date(),
                created_by: {
                    id: this.currentUser.id
                }
            };
            
            this.hourCreationState = 'create';
        },
        onSubmit(result) {
            this.selectedHour = null;
            if (result.action === 'create') {
                this.createHour(result.event);
            } else if (result.action === 'edit') {
                this.updateHour(result.event);
            }
        },
        updateHour (event) {
            var timeentry = HourService.eventToTimeentry(event, this.$store.getters["filterStore/getCurrentProject"]);
            HourService.update(timeentry).then(() => {
                this.refreshData();
                this.$store.commit('setAlert', {title: this.$gettext("Hour Edited"), text:this.$gettext("Your time entry has been edited"), variant:"success",duration:4});
            }).catch((error) => {
                let errorTxt = this.$gettext('Something went wrong.');
                
                if (error.non_field_errors)
                    errorTxt = error.non_field_errors; // the response should already be translated
                else if (error.data && error.data.created_by)
                    errorTxt = this.$gettext('The user who created this entry is no longer connected to this company. Please select a new user to be assigned to this entry or reconnect the user by adding a team member.');
                
                this.$store.commit('setAlert', { 
                    title: this.$gettext("Error"),
                    text: errorTxt,
                    variant: "danger",
                    duration: 4 
                });
            });
        },
        createHour(event) {
            var timeentry = HourService.eventToTimeentry(event, null);

            HourService.create(timeentry).then(() => {
                this.refreshData();
                this.$store.commit('setAlert', {title: this.$gettext("Hour Created"), text:this.$gettext("A new hour has been created"), variant:"success",duration:4});
            }).catch(() => {
                this.$store.commit('setAlert', { 
                    title: this.$gettext('Error'),
                    text: this.$gettext('The hour could not be created.'),
                    variant: "danger",
                    duration: 4 
                });
            });
        },
        openCopyModal(event) {
            event.start = Date.parse(event.date);

            this.hourToCopy = event;
            this.$refs.copy_hour_modal.showModal = true;
        },
        onCopyHour(newHour) {
            this.createHour(newHour);
        },
        cancelEventCreation(action) {
            this.selectedEvent = null;
        },
        cancelConfirmationDialog() {
            this.recurrentUpdateResult = null;
            this.recurrentDeleteResult = null;
            this.selectedHour = null;
        },
        onDelete(event) {
            if (event.id) {
                HourService.destroy(event.id).then(() => {
                    this.refreshData();
                    this.$store.commit('setAlert', {title: this.$gettext("Hour Deleted"), text:this.$gettext("Deleted an hour"), variant:"danger",duration:4});
                })
            }
        },
        onDeleteRecurrentHour(event) {
            this.recurrentDeleteResult = event;
            this.$refs.recurrent_hour_delete_confirmation_modal.open();
        },
        onRecurrentHourDeleteConfirmation(result) {
            if (this.recurrentDeleteResult.id) {
                let params = {choice: result};
                HourService.destroyWithParams(this.recurrentDeleteResult.id, params).then(() => {
                    this.refreshData();
                });
            }
        },
        onRecurrentHourUpdateConfirmation(result) {
            if (result === "one") {
                this.recurrentUpdateResult.selected_hour.date = this.recurrentUpdateResult.selected_hour.newDate;
                delete this.recurrentUpdateResult.selected_hour.newDate;
                delete this.recurrentUpdateResult.selected_hour.oldDate;
                
                HourService.update(this.recurrentUpdateResult.selected_hour).then(() => {
                    this.refreshData();
                    this.$store.commit('setAlert', {title: this.$gettext("Hour Edited"), text:this.$gettext("Your time entry has been edited"), variant:"success",duration:4});
                });

                this.recurrentUpdateResult = null;
                this.selectedHour = null;
            } else if (result === "all") {
                this.recurrentUpdateResult.selected_hour.date = this.recurrentUpdateResult.selected_hour.oldDate;
                delete this.recurrentUpdateResult.selected_hour.newDate;
                delete this.recurrentUpdateResult.selected_hour.oldDate;

                this.recurrentUpdateResult["selected_hour_date"] = this.recurrentUpdateResult.selected_hour.date;

                if(this.recurrentUpdateResult.template_time_entry.project && this.recurrentUpdateResult.template_time_entry.project.id) {
                    this.recurrentUpdateResult.template_time_entry.project = this.recurrentUpdateResult.template_time_entry.project.id;
                }
                
                HourService.updateRecurrentHour(this.recurrentUpdateResult).then(() => {
                    this.refreshData();
                    this.$store.commit('setAlert', {title: this.$gettext("Hours Edited"), text:this.$gettext("The hours were edited"), variant:"success",duration:4});
                    this.recurrentUpdateResult = null;
                    this.selectedHour = null;
                });
            }
        },
        onSubmitRecurrentHour(result) {
            if (result.action === "create") {
                HourService.createRecurrentHour(result).then(() => {
                    this.refreshData();
                    this.$store.commit('setAlert', {title: this.$gettext("Hours Created"), text:this.$gettext("New hours have been created"), variant:"success",duration:4});
                    this.selectedHour = null;
                });
            } else if (result.action === "edit") {
                this.recurrentUpdateResult = result;
                
                this.recurrentUpdateResult.selected_hour.newDate = result.selected_hour.date;
                this.recurrentUpdateResult.selected_hour.oldDate = this.selectedHour.date;
                delete this.recurrentUpdateResult.selected_hour.date;
                
                this.$refs.recurrent_hour_update_confirmation_modal.open();
            }
        },
        isInViewport(element_id) {
            let element = document.getElementById(element_id);
            let rect = element.getBoundingClientRect();
            let html = document.documentElement;
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth)
            );
        },
        handleScroll() {
            if (!this.fetchingHours && this.nextHoursUrl) {
                let loadNewHours = this.isInViewport('load_more_button');
                if (loadNewHours) {
                    this.fetchingHours = true;
                    
                    HourService.loadNextPage(this.nextHoursUrl).then((response) => {
                        this.fetchingHours = false;
                        this.hourList = this.hourList.concat(response.data.results);

                        if (response.data.next)
                            this.nextHoursUrl = response.data.next;
                        else
                            this.nextHoursUrl = null;
                    });
                }
            }
        }
    }
}
</script>

<style scoped>

</style>